'use client';
import { useCallback } from 'react';
import posthog, { type Properties } from 'posthog-js';

export interface AnalyticsEvent {
  eventName: string;
  properties?: Properties;
  userId?: string;
  distinctId?: string;
}

export interface UseAnalyticsReturn {
  track: (event: AnalyticsEvent) => void;
  capture: (eventName: string, properties?: Properties) => void;
  identify: (userId: string, properties?: Properties) => void;
}

const communityNavigationTimingEvents = new Set([
  'community_navigation_started',
  'community_detail_settled',
  'community_header_rendered',
  'community_first_post_rendered',
  'community_navigation_hint_rejected',
  'community_navigation_click',
  'community_navigation_shell',
  'community_navigation_fresh_authority',
  'community_navigation_target_visible',
  'community_navigation_target_settled',
  'community_navigation_correct_header',
  'community_navigation_highlight_dwell_complete',
]);
const communityNavigationMismatchCategories = new Set([
  'route',
  'profile',
  'creator',
  'conversation',
  'thread',
  'type',
  'privacy',
]);
const postNavigationTimingEvents = new Set([
  'post_navigation_started',
  'post_shell_committed',
  'post_content_committed',
  'post_access_settled',
  'post_single_post_settled',
  'post_community_detail_settled',
  'post_comments_settled',
  'post_detail_settled',
]);

export const useAnalytics = (): UseAnalyticsReturn => {
  const track = useCallback((event: AnalyticsEvent) => {
    const eventData = {
      ...event.properties,
      timestamp: Date.now(),
      ...(event.userId && { user_id: event.userId }),
    };

    posthog.capture(event.eventName, eventData);
  }, []);

  const capture = useCallback((eventName: string, properties?: Properties) => {
    const eventData = postNavigationTimingEvents.has(eventName)
      ? {
          navigation_id: properties?.navigation_id,
          source: properties?.source,
          handoff_outcome: properties?.handoff_outcome,
          outcome: properties?.outcome,
          duration_ms: properties?.duration_ms,
        }
      : communityNavigationTimingEvents.has(eventName)
      ? {
          navigation_id: properties?.navigation_id,
          surface: properties?.surface,
          source: properties?.source,
          auth_state: properties?.auth_state,
          stage: properties?.stage,
          outcome: properties?.outcome,
          ...(typeof properties?.mismatch_category === 'string' &&
          communityNavigationMismatchCategories.has(properties.mismatch_category)
            ? { mismatch_category: properties.mismatch_category }
            : {}),
          duration_ms: properties?.duration_ms,
        }
      : {
          ...properties,
          timestamp: Date.now(),
        };

    posthog.capture(eventName, eventData);
  }, []);

  const identify = useCallback((userId: string, properties?: Properties) => {
    posthog.identify(userId, properties);
  }, []);

  return {
    track,
    capture,
    identify,
  };
};
